/**
 * Deterministic retail-catalogue product identity helpers.
 *
 * Field meanings:
 * - brand: manufacturer’s canonical brand (stored/rendered separately)
 * - model: manufacturer’s canonical model formatting
 * - name / customerFriendlyName: plain-English identity shown to customers
 * - displayName / catalogueName: `${model} — ${customerFriendlyName}` (no brand)
 *
 * brandNorm / modelNorm / matchKey / identityKey are comparison helpers only.
 * Canonical model formatting for display is never forced by guessing spacing.
 * Brand + Model uniqueness is scoped by brandNorm — identical model codes under
 * different brands remain separate products.
 */

/** Reviewed brand aliases → canonical display brand. Comparison keys are lowercase. */
export const REVIEWED_BRAND_ALIASES: Readonly<Record<string, string>> = {
  "t+a": "T+A",
  "t a": "T+A",
  "t-a": "T+A",
  "t & a": "T+A",
  "ta elektroakustik": "T+A",
  "t+a elektroakustik": "T+A",
  "2n": "2N",
  "2 n": "2N",
  "2n telekomunikace": "2N",
};

export type CatalogueIdentityConfidence = "high" | "medium" | "low";

export type CatalogueIdentityStatus =
  | "exact_match"
  | "identified"
  | "needs_confirmation"
  | "blocked_duplicate";

export type CatalogueIdentityEvidenceSourceType =
  | "manufacturer"
  | "distributor"
  | "catalogue"
  | "other";

export type CatalogueIdentityEvidence = {
  title: string;
  url: string;
  sourceType: CatalogueIdentityEvidenceSourceType;
  snippet: string;
};

export type CatalogueIdentityMatchKind =
  | "exact_active"
  | "pending_approval"
  | "inactive"
  | "linked_duplicate"
  | "conflicting_identity";

export type CatalogueIdentityExistingMatch = {
  id: number;
  brand: string;
  model: string;
  name: string;
  displayName: string | null;
  isActive: boolean;
  isCustom: boolean;
  approvalStatus: string | null;
  duplicateStatus: string | null;
  canonicalCatalogueItemId: number | null;
  /** When match was a linked duplicate, this is the resolved canonical row id. */
  resolvedCanonicalId: number | null;
  matchKind: CatalogueIdentityMatchKind;
  blockMessage: string;
  canUseExisting: boolean;
};

export type CatalogueProductIdentitySuggestion = {
  canonicalBrand: string;
  canonicalModel: string;
  /** Customer Friendly Name (stored in catalogue_items.name). */
  productName: string;
  customerFriendlyName: string;
  productType: string | null;
  proposedDisplayName: string;
  confidence: CatalogueIdentityConfidence;
  status: CatalogueIdentityStatus;
  evidence: CatalogueIdentityEvidence[];
  existingMatches: CatalogueIdentityExistingMatch[];
  warnings: string[];
  blockMessage: string | null;
  requiresAdminDuplicateReview: boolean;
  /** Comparison keys used for duplicate detection. */
  brandNorm: string;
  modelNorm: string;
  matchKey: string;
  identityKey: string;
  originalBrand: string;
  originalModel: string;
};

const UNICODE_PUNCT_MAP: Record<string, string> = {
  "\u2010": "-",
  "\u2011": "-",
  "\u2012": "-",
  "\u2013": "-",
  "\u2014": "-",
  "\u2212": "-",
  "\u00A0": " ",
  "\u202F": " ",
  "\u2009": " ",
  "\u2007": " ",
  "\uFF0B": "+",
  "\uFF0D": "-",
};

export function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

export function normalizeUnicodePunctuation(value: string): string {
  let out = "";
  for (const ch of value) {
    out += UNICODE_PUNCT_MAP[ch] ?? ch;
  }
  return out;
}

export function normalizeIdentityText(value: string | null | undefined): string {
  return collapseWhitespace(normalizeUnicodePunctuation(String(value ?? "")));
}

function aliasLookupKey(brand: string): string {
  return normalizeIdentityText(brand).toLowerCase();
}

/** Apply a reviewed brand alias when one exists; otherwise return cleaned input. */
export function applyReviewedBrandAlias(brand: string | null | undefined): string {
  const cleaned = normalizeIdentityText(brand);
  if (!cleaned) return "";
  const alias = REVIEWED_BRAND_ALIASES[aliasLookupKey(cleaned)];
  return alias ?? cleaned;
}

/** Comparison brand key: alias-aware, lowercase, collapsed whitespace. */
export function normalizeBrandNorm(brand: string | null | undefined): string {
  return applyReviewedBrandAlias(brand).toLowerCase();
}

/**
 * Comparison-only model key: case-insensitive, ignores spaces/hyphens/harmless punctuation.
 * Preserves alphanumerics and meaningful symbols such as + / .
 */
export function compactModelKey(model: string | null | undefined): string {
  return normalizeIdentityText(model)
    .toLowerCase()
    .replace(/[\s\-_.]/g, "")
    .replace(/[^a-z0-9+/]/g, "");
}

/**
 * Stored modelNorm: compact comparison key so equivalent models collide for matching.
 * Canonical manufacturer formatting remains in `model`.
 */
export function normalizeModelNorm(model: string | null | undefined): string {
  return compactModelKey(model);
}

export function buildCatalogueMatchKey(
  brand: string | null | undefined,
  model: string | null | undefined,
): string {
  const brandNorm = normalizeBrandNorm(brand);
  const modelNorm = normalizeModelNorm(model);
  if (!brandNorm || !modelNorm) return "";
  return `${brandNorm}|${modelNorm}`;
}

/** Stable uniqueness key for Brand + compact Model (never collapses across brands). */
export function buildCatalogueIdentityKey(
  brand: string | null | undefined,
  model: string | null | undefined,
): string {
  return buildCatalogueMatchKey(brand, model);
}

export function modelsAreEquivalent(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = compactModelKey(a);
  const right = compactModelKey(b);
  return Boolean(left && right && left === right);
}

function stripBrandPrefix(text: string, brand: string): string {
  const cleaned = normalizeIdentityText(text);
  const b = normalizeIdentityText(brand);
  if (!cleaned || !b) return cleaned;
  const re = new RegExp(`^${escapeRegExp(b)}\\s*[-–—:/|]?\\s*`, "i");
  return cleaned.replace(re, "").trim() || cleaned;
}

function stripModelToken(text: string, model: string): string {
  const cleaned = normalizeIdentityText(text);
  const m = normalizeIdentityText(model);
  if (!cleaned || !m) return cleaned;
  if (compactModelKey(cleaned) === compactModelKey(m)) return cleaned;
  const re = new RegExp(`(?:^|\\s)${escapeRegExp(m)}(?:\\s|$)`, "i");
  const stripped = cleaned.replace(re, " ").replace(/\s+/g, " ").trim();
  return stripped || cleaned;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function productNameContainsModel(productName: string, model: string): boolean {
  const name = normalizeIdentityText(productName).toLowerCase();
  const m = normalizeIdentityText(model).toLowerCase();
  if (!name || !m) return false;
  if (name.includes(m)) return true;
  const compactName = compactModelKey(productName);
  const compactM = compactModelKey(model);
  return Boolean(compactM && compactName.includes(compactM));
}

/**
 * Deterministic catalogue display name:
 * `${canonicalModel} — ${productName}` when both present and materially different.
 * Never repeats brand. Avoids repeating model when the product name already contains it.
 */
export function buildCatalogueDisplayName(input: {
  brand?: string | null;
  model?: string | null;
  productName?: string | null;
  name?: string | null;
}): string {
  const brand = applyReviewedBrandAlias(input.brand);
  const model = normalizeIdentityText(input.model);
  let productName = normalizeIdentityText(input.productName ?? input.name);

  if (productName) {
    productName = stripBrandPrefix(productName, brand);
    if (model && productNameContainsModel(productName, model)) {
      // Keep product name as-is when it already embeds the model; do not also prefix model.
      const withoutModel = stripModelToken(productName, model);
      // If stripping leaves a meaningful type phrase, prefer "Model — Type".
      if (
        withoutModel &&
        compactModelKey(withoutModel) !== compactModelKey(model) &&
        withoutModel.toLowerCase() !== productName.toLowerCase()
      ) {
        return `${model} — ${withoutModel}`;
      }
      return productName;
    }
  }

  if (model && productName && model.toLowerCase() !== productName.toLowerCase()) {
    return `${model} — ${productName}`;
  }
  if (model) return model;
  if (productName) return productName;
  return brand || "Product";
}

export function buildIdentityNormFields(input: {
  brand: string;
  model: string;
}): {
  brandNorm: string;
  modelNorm: string;
  matchKey: string;
  identityKey: string;
  canonicalBrand: string;
} {
  const canonicalBrand = applyReviewedBrandAlias(input.brand);
  const brandNorm = normalizeBrandNorm(canonicalBrand);
  const modelNorm = normalizeModelNorm(input.model);
  const matchKey = buildCatalogueMatchKey(canonicalBrand, input.model);
  return {
    canonicalBrand,
    brandNorm,
    modelNorm,
    matchKey,
    identityKey: matchKey,
  };
}

export function classifyExistingMatchKind(row: {
  isActive?: boolean | null;
  isCustom?: boolean | null;
  approvalStatus?: string | null;
  duplicateStatus?: string | null;
  canonicalCatalogueItemId?: number | null;
  brand?: string | null;
  model?: string | null;
  name?: string | null;
  expectedBrandNorm?: string;
  expectedModelNorm?: string;
  /** Material conflict on brand/model identity data after compact compare. */
  hasMaterialIdentityConflict?: boolean | null;
}): CatalogueIdentityMatchKind {
  const dup = String(row.duplicateStatus ?? "none");
  if (dup === "linked" || (row.canonicalCatalogueItemId != null && row.canonicalCatalogueItemId > 0)) {
    return "linked_duplicate";
  }
  if (row.hasMaterialIdentityConflict) return "conflicting_identity";
  if (row.isActive === false) return "inactive";
  if (row.isCustom && String(row.approvalStatus ?? "").toLowerCase() === "pending") {
    return "pending_approval";
  }

  const brandNorm = normalizeBrandNorm(row.brand);
  const modelNorm = normalizeModelNorm(row.model);
  if (
    row.expectedBrandNorm &&
    row.expectedModelNorm &&
    brandNorm &&
    modelNorm &&
    (brandNorm !== row.expectedBrandNorm || modelNorm !== row.expectedModelNorm)
  ) {
    return "conflicting_identity";
  }
  return "exact_active";
}

export function blockMessageForMatchKind(kind: CatalogueIdentityMatchKind): string {
  switch (kind) {
    case "pending_approval":
      return "This product has already been submitted and is awaiting approval.";
    case "inactive":
      return "This product already exists but is inactive. Ask an admin to reactivate it.";
    case "linked_duplicate":
      return "This product already exists in the catalogue.";
    case "conflicting_identity":
      return "This product identity conflicts with an existing catalogue record and requires admin duplicate review.";
    case "exact_active":
    default:
      return "This product already exists in the catalogue.";
  }
}

export function canUseExistingForMatchKind(kind: CatalogueIdentityMatchKind): boolean {
  return kind === "exact_active" || kind === "linked_duplicate";
}

/** Detect unnecessary brand/model repetition in a Customer Friendly Name. */
export function customerFriendlyNameRepetitionIssues(input: {
  brand?: string | null;
  model?: string | null;
  customerFriendlyName?: string | null;
}): string[] {
  const brand = applyReviewedBrandAlias(input.brand);
  const model = normalizeIdentityText(input.model);
  const name = normalizeIdentityText(input.customerFriendlyName);
  const issues: string[] = [];
  if (!name) {
    issues.push("Customer Friendly Name is required.");
    return issues;
  }
  if (brand && name.toLowerCase().includes(brand.toLowerCase())) {
    issues.push("Customer Friendly Name repeats the brand. Brand is shown separately.");
  }
  if (model && productNameContainsModel(name, model)) {
    issues.push("Customer Friendly Name repeats the model number. Model Number is shown separately.");
  }
  return issues.filter((m) => m !== "Customer Friendly Name is required.");
}

export function sanitizeCustomerFriendlyName(input: {
  brand?: string | null;
  model?: string | null;
  customerFriendlyName?: string | null;
}): string {
  const brand = applyReviewedBrandAlias(input.brand);
  const model = normalizeIdentityText(input.model);
  let name = normalizeIdentityText(input.customerFriendlyName);
  if (!name) return "";
  name = stripBrandPrefix(name, brand);
  if (model && productNameContainsModel(name, model)) {
    const withoutModel = stripModelToken(name, model);
    if (withoutModel && compactModelKey(withoutModel) !== compactModelKey(model)) {
      return withoutModel;
    }
  }
  return name;
}

export function canAutoUpdateCustomerFriendlyName(manuallyEdited?: boolean | null): boolean {
  return manuallyEdited !== true;
}
